import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

type Role = "operador" | "porteira" | "admin";

interface UserAccount {
  id: string;
  username: string;
  role: Role;
}

interface UserAccountContextType {
  user: UserAccount | null;
  login: (username: string, password_hash: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const UserAccountContext = createContext<UserAccountContextType | undefined>(undefined);

export const UserAccountProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem("tupja_user_account");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem("tupja_user_account");
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (username: string, password_hash: string) => {
    const { data, error } = await supabase
      .from("user_accounts")
      .select("id, username, role")
      .eq("username", username)
      .eq("password_hash", password_hash)
      .maybeSingle();

    if (error || !data) return false;

    const userData = data as UserAccount;
    setUser(userData);
    localStorage.setItem("tupja_user_account", JSON.stringify(userData));
    return true;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("tupja_user_account");
  };

  return (
    <UserAccountContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </UserAccountContext.Provider>
  );
};

export const useUserAccount = () => {
  const context = useContext(UserAccountContext);
  if (context === undefined) {
    throw new Error("useUserAccount must be used within a UserAccountProvider");
  }
  return context;
};
